import { Box3, Frustum, Group, Matrix4, PerspectiveCamera, Sphere, Vector3, type Texture } from "three";
import { unionBbox2, type Bbox2 } from "../geo/bbox";
import type { SceneOrigin } from "../geo/origin";
import { isNoData, type GeoRaster } from "../io/geoRaster";
import { elevationRange, sampleHeights } from "../io/heights";
import { isAbort } from "../util/async";
import { TerrainTile, type UvRect } from "./TerrainTile";
import { textureFromElevation, textureFromRaster } from "./textures";

export interface TerrainLayerOptions {
  /** Height-field resolution per tile side. 48 keeps a tile at ~4.6k triangles. */
  cells?: number;
  /** Tiles are sized so the grid lands near this count, whatever the site extent. */
  targetTiles?: number;
  /** Ceiling on GPU memory spent on detail textures. */
  textureBudgetBytes?: number;
  maxTextureSize?: number;
  /** Elevation used where no surface model is available. */
  fallbackHeight?: number;
  /** Called whenever visible content changed and a repaint is needed. */
  onChange?: () => void;
}

export interface TerrainStats {
  tiles: number;
  tilesWithGeometry: number;
  detailTextures: number;
  textureBytes: number;
  pending: number;
}

interface TextureRequest {
  tile: TerrainTile;
  level: number;
  size: number;
  priority: number;
}

const MAX_CONCURRENT_TEXTURES = 4;
const LOD_INTERVAL_MS = 180;
/** Largest edge of the single texture that covers the whole site. */
const BASE_TEXTURE_MAX = 2048;

/**
 * A tiled, textured surface built from an orthophoto and/or a surface model.
 *
 * The grid is uniform rather than a quadtree: neighbouring tiles evaluate their shared
 * edges at identical coordinates so the surface never cracks, and the level of detail that
 * actually matters — imagery sharpness — is handled per tile by swapping textures read
 * from the GeoTIFF's overview pyramid. A single low-resolution texture covering the whole
 * site is uploaded first so the layer is complete from the first frame.
 */
export class TerrainLayer {
  readonly group = new Group();
  readonly tiles: TerrainTile[] = [];
  extent: Bbox2 = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  minHeight = NaN;
  maxHeight = NaN;
  /**
   * Elevation the whole surface is drawn flat at — the visual mesh never follows real
   * relief (see {@link TerrainTile.buildGeometry}), so this is also what the swisstopo
   * backdrop is placed at, keeping the two from ever showing a visible step between them.
   */
  groundHeight = 0;
  /** Ground sample distance of the imagery, in CRS units. */
  orthoResolution = NaN;
  demResolution = NaN;

  private readonly appearance = { opacity: 1, wireframe: false };
  private baseTexture: Texture | null = null;
  private baseExtent: Bbox2 | null = null;
  private readonly cells: number;
  private readonly maxTextureSize: number;
  private readonly textureBudget: number;
  private tileSizeX = 1;
  private tileSizeY = 1;
  private inFlight = 0;
  private lastLod = 0;
  private frame = 0;
  private disposed = false;
  private readonly frustum = new Frustum();
  private readonly projScreen = new Matrix4();
  private readonly sphere = new Sphere();
  private readonly tmp = new Vector3();

  constructor(
    readonly origin: SceneOrigin,
    readonly ortho: GeoRaster | null,
    readonly dem: GeoRaster | null,
    private readonly options: TerrainLayerOptions = {},
  ) {
    this.cells = options.cells ?? 48;
    this.maxTextureSize = options.maxTextureSize ?? 1024;
    this.textureBudget = options.textureBudgetBytes ?? 640 * 1024 * 1024;

    this.group.name = "terrain";
    this.group.renderOrder = 0;
  }

  get tileSize(): number {
    return Math.max(this.tileSizeX, this.tileSizeY);
  }

  /**
   * Reads metadata, lays out the tile grid, uploads the overview texture and builds the
   * (flat) tile geometry. Real elevations for height queries are filled in afterwards by
   * {@link streamHeights}; they never change what's drawn.
   */
  async build(onProgress?: (done: number, total: number, label: string) => void): Promise<void> {
    const extents: Bbox2[] = [];
    if (this.ortho) extents.push(this.ortho.bbox);
    if (this.dem) extents.push(this.dem.bbox);
    if (extents.length === 0) throw new Error("Terrain layer needs an orthophoto or a surface model");

    this.extent = extents.reduce((acc, b) => unionBbox2(acc, b), { ...extents[0] });
    this.orthoResolution = this.ortho?.resolution ?? NaN;
    this.demResolution = this.dem?.resolution ?? NaN;

    let fallbackHeight = this.options.fallbackHeight ?? 0;
    if (this.dem) {
      onProgress?.(0, 1, "Reading elevation range");
      const range = await elevationRange(this.dem);
      this.minHeight = range.min;
      this.maxHeight = range.max;
      if (Number.isFinite(range.min) && Number.isFinite(range.max)) {
        fallbackHeight = (range.min + range.max) / 2;
      }
    }
    this.groundHeight = Number.isFinite(fallbackHeight) ? fallbackHeight : 0;

    this.origin.setOnce(
      (this.extent.minX + this.extent.maxX) / 2,
      (this.extent.minY + this.extent.maxY) / 2,
      this.groundHeight,
    );

    this.layoutGrid();
    await this.buildBaseTexture(this.groundHeight);
    this.attachBaseTextures();

    for (const tile of this.tiles) {
      tile.buildGeometry(this.origin, this.groundHeight, this.cells);
      this.group.add(tile.mesh);
    }
  }

  /** Chooses a tile size that keeps the grid near `targetTiles` for any site extent. */
  private layoutGrid(): void {
    const width = this.extent.maxX - this.extent.minX;
    const height = this.extent.maxY - this.extent.minY;
    const target = this.options.targetTiles ?? 256;
    const ideal = Math.sqrt((width * height) / target) || Math.max(width, height);

    const columns = Math.max(1, Math.min(96, Math.round(width / ideal) || 1));
    const rows = Math.max(1, Math.min(96, Math.round(height / ideal) || 1));
    this.tileSizeX = width / columns;
    this.tileSizeY = height / rows;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const bbox: Bbox2 = {
          minX: this.extent.minX + col * this.tileSizeX,
          maxX: this.extent.minX + (col + 1) * this.tileSizeX,
          maxY: this.extent.maxY - row * this.tileSizeY,
          minY: this.extent.maxY - (row + 1) * this.tileSizeY,
        };
        this.tiles.push(new TerrainTile(col, row, bbox, this.appearance));
      }
    }
  }

  /**
   * Uploads one texture covering the whole site, read from the coarsest overview.
   * Without it the viewer would show untextured grey until per-tile reads land.
   */
  private async buildBaseTexture(fallbackHeight: number): Promise<void> {
    if (this.ortho) {
      const level = this.pickBaseLevel(this.ortho);
      const lvl = this.ortho.levels[level];
      const window = await this.ortho.readWindow(level, [0, 0, lvl.width, lvl.height], baseSize(lvl));
      this.baseTexture = textureFromRaster(window, { hasAlpha: this.ortho.hasAlpha });
      this.baseExtent = this.ortho.bbox;
      return;
    }
    if (this.dem) {
      const level = this.pickBaseLevel(this.dem);
      const lvl = this.dem.levels[level];
      const window = await this.dem.readWindow(level, [0, 0, lvl.width, lvl.height], {
        samples: [0],
        ...baseSize(lvl),
      });
      const values = toFloatArray(window.data, this.dem.noData);
      const min = Number.isFinite(this.minHeight) ? this.minHeight : fallbackHeight - 1;
      const max = Number.isFinite(this.maxHeight) ? this.maxHeight : fallbackHeight + 1;
      this.baseTexture = textureFromElevation(values, window.width, window.height, {
        min,
        max,
        // The window may have been downsampled, so derive the cell size from what was read.
        cellSize: (this.dem.bbox.maxX - this.dem.bbox.minX) / Math.max(1, window.width),
      });
      this.baseExtent = this.dem.bbox;
    }
  }

  /**
   * Coarsest level that still has enough pixels to be a usable overview image. The read is
   * additionally capped by {@link baseSize}, so a file with no overviews at all is
   * downsampled on read rather than pulled at full resolution.
   */
  private pickBaseLevel(raster: GeoRaster): number {
    for (let i = raster.levelCount - 1; i >= 0; i--) {
      const lvl = raster.levels[i];
      if (Math.max(lvl.width, lvl.height) >= 512 || i === 0) return i;
    }
    return 0;
  }

  private attachBaseTextures(): void {
    if (!this.baseTexture || !this.baseExtent) return;
    for (const tile of this.tiles) {
      tile.setBaseTexture(this.baseTexture.clone(), uvRectFor(this.baseExtent, tile.bbox));
    }
  }

  /**
   * Fills in the real height field tile by tile, nearest the site centre first — used only
   * to answer height queries (see {@link heightAt}); the flat visual mesh built in
   * {@link build} never changes. Yields between tiles so a slow fetch doesn't block queries
   * against the tiles that have already arrived.
   */
  async streamHeights(
    onProgress?: (done: number, total: number) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!this.dem) return;
    const level = this.pickHeightLevel();
    const centreX = (this.extent.minX + this.extent.maxX) / 2;
    const centreY = (this.extent.minY + this.extent.maxY) / 2;
    const ordered = [...this.tiles].sort(
      (a, b) => distanceToCentre(a.bbox, centreX, centreY) - distanceToCentre(b.bbox, centreX, centreY),
    );

    let done = 0;
    for (const tile of ordered) {
      if (this.disposed || signal?.aborted) return;
      try {
        const grid = await sampleHeights(this.dem, level, tile.bbox, this.cells, signal);
        if (this.disposed) return;
        tile.setHeightGrid(grid);
      } catch (error) {
        if (isAbort(error)) return;
        console.warn(`[terrain] tile ${tile.key} heights failed:`, error);
      }
      onProgress?.(++done, ordered.length);
    }
  }

  /** Real elevation at (east, north) from the surface model, or `null` with no data there. */
  heightAt(east: number, north: number): number | null {
    for (const tile of this.tiles) {
      const { minX, minY, maxX, maxY } = tile.bbox;
      if (east < minX || east > maxX || north < minY || north > maxY) continue;
      const height = tile.heightAt(east, north);
      if (height !== null) return height;
    }
    return null;
  }

  /** Height-field level: one DEM sample per grid cell, no finer. */
  private pickHeightLevel(): number {
    if (!this.dem) return 0;
    const cellSize = Math.min(this.tileSizeX, this.tileSizeY) / this.cells;
    return this.dem.levelForResolution(cellSize);
  }

  /**
   * Per-frame level of detail: works out how large each tile is on screen, requests a
   * sharper texture for the ones that need it and reclaims memory from the rest.
   */
  update(camera: PerspectiveCamera, viewportHeight: number, now: number): void {
    if (this.disposed || !this.group.visible || !this.ortho) return;
    if (now - this.lastLod < LOD_INTERVAL_MS) return;
    this.lastLod = now;
    this.frame++;

    // Streaming runs on its own cadence rather than inside the render loop, so the camera
    // matrices cannot be assumed fresh from a previous frame.
    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    this.group.updateMatrixWorld();
    this.projScreen.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projScreen);

    // Pixels per metre at unit distance, from the vertical field of view.
    const pixelsPerRadian = viewportHeight / (2 * Math.tan(((camera.fov / 2) * Math.PI) / 180));
    const requests: TextureRequest[] = [];
    let spent = 0;

    for (const tile of this.tiles) {
      spent += tile.textureCost;
      const geometry = tile.mesh.geometry;
      if (!tile.mesh.visible || !geometry.boundingSphere) {
        tile.screenSize = 0;
        continue;
      }
      this.sphere.copy(geometry.boundingSphere).applyMatrix4(this.group.matrixWorld);
      if (!this.frustum.intersectsSphere(this.sphere)) {
        tile.screenSize = 0;
        continue;
      }

      const distance = Math.max(0.5, this.tmp.copy(this.sphere.center).distanceTo(camera.position));
      const screenSize = (this.sphere.radius * 2 * pixelsPerRadian) / distance;
      tile.screenSize = screenSize;
      tile.lastVisible = this.frame;

      const desired = clampPow2(screenSize, 128, this.maxTextureSize);
      if (desired > tile.textureSize && !tile.loading) {
        const level = this.ortho.levelForResolution(Math.max(this.tileSizeX, this.tileSizeY) / desired);
        // Skip a request that would not actually add pixels over what is already shown.
        if (level !== tile.textureLevel || desired > tile.textureSize) {
          requests.push({ tile, level, size: desired, priority: screenSize });
        }
      }
    }

    if (spent > this.textureBudget) this.evict(spent - this.textureBudget);

    requests.sort((a, b) => b.priority - a.priority);
    for (const request of requests) {
      if (this.inFlight >= MAX_CONCURRENT_TEXTURES) break;
      void this.loadTileTexture(request);
    }
  }

  private async loadTileTexture(request: TextureRequest): Promise<void> {
    const { tile, level, size } = request;
    if (!this.ortho || tile.loading) return;
    tile.loading = true;
    this.inFlight++;
    const controller = new AbortController();
    tile.abort = controller;
    try {
      const lvl = this.ortho.levels[level];
      const rect = pixelWindowFor(this.ortho, level, tile.bbox);
      // A tile can be larger than the whole overview at coarse levels; never ask for more
      // output pixels than the source can supply.
      const outWidth = Math.min(size, Math.max(16, Math.ceil(rect.width)));
      const outHeight = Math.min(size, Math.max(16, Math.ceil(rect.height)));
      if (rect.width <= 0 || rect.height <= 0 || lvl.width === 0) return;

      const window = await this.ortho.readWindow(level, rect.window, {
        outWidth,
        outHeight,
        clampWindow: false,
        signal: controller.signal,
      });
      if (this.disposed || controller.signal.aborted || window.width === 0) return;

      const texture = textureFromRaster(window, { hasAlpha: this.ortho.hasAlpha });
      tile.setDetailTexture(texture, rect.uv, level, Math.max(outWidth, outHeight));
      this.options.onChange?.();
    } catch (error) {
      if (!isAbort(error)) console.warn(`[terrain] tile ${tile.key} texture failed:`, error);
    } finally {
      tile.loading = false;
      tile.abort = null;
      this.inFlight--;
    }
  }

  /** Frees detail textures from the tiles that have been out of view longest. */
  private evict(bytesToFree: number): void {
    const candidates = this.tiles
      .filter((tile) => tile.textureCost > 0 && tile.screenSize === 0)
      .sort((a, b) => a.lastVisible - b.lastVisible);
    let freed = 0;
    for (const tile of candidates) {
      if (freed >= bytesToFree) break;
      freed += tile.textureCost;
      tile.releaseDetail();
    }
  }

  /** The flat mesh's own footprint — real elevation range plays no part since it isn't drawn. */
  bounds(): Box3 {
    const box = new Box3();
    const height = this.origin.sceneY(this.groundHeight);
    box.setFromPoints([
      new Vector3(this.origin.sceneX(this.extent.minX), height, this.origin.sceneZ(this.extent.minY)),
      new Vector3(this.origin.sceneX(this.extent.maxX), height, this.origin.sceneZ(this.extent.maxY)),
    ]);
    return box;
  }

  stats(): TerrainStats {
    let withGeometry = 0;
    let detail = 0;
    let bytes = 0;
    for (const tile of this.tiles) {
      if (tile.mesh.visible) withGeometry++;
      if (tile.textureCost > 0) {
        detail++;
        bytes += tile.textureCost;
      }
    }
    return {
      tiles: this.tiles.length,
      tilesWithGeometry: withGeometry,
      detailTextures: detail,
      textureBytes: bytes,
      pending: this.inFlight,
    };
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  setOpacity(opacity: number): void {
    this.appearance.opacity = opacity;
    for (const tile of this.tiles) tile.setOpacity(opacity);
  }

  setWireframe(enabled: boolean): void {
    this.appearance.wireframe = enabled;
    for (const tile of this.tiles) tile.setWireframe(enabled);
  }

  dispose(): void {
    this.disposed = true;
    for (const tile of this.tiles) {
      tile.dispose();
      this.group.remove(tile.mesh);
    }
    this.tiles.length = 0;
    this.baseTexture?.dispose();
    this.baseTexture = null;
  }
}

/** UV rectangle occupied by `tile` inside a texture spanning `container`. */
function uvRectFor(container: Bbox2, tile: Bbox2): UvRect {
  const width = container.maxX - container.minX;
  const height = container.maxY - container.minY;
  return {
    offsetX: (tile.minX - container.minX) / width,
    offsetY: (tile.minY - container.minY) / height,
    repeatX: (tile.maxX - tile.minX) / width,
    repeatY: (tile.maxY - tile.minY) / height,
  };
}

/**
 * Integer pixel window covering `bbox` plus the UV rectangle that maps the tile back onto
 * exactly its own ground footprint inside that window.
 *
 * Rounding the window outwards and correcting through UVs — rather than snapping the tile
 * to pixel edges — is what keeps neighbouring tiles' imagery continuous.
 */
function pixelWindowFor(
  raster: GeoRaster,
  level: number,
  bbox: Bbox2,
): { window: [number, number, number, number]; uv: UvRect; width: number; height: number } {
  const topLeft = raster.crsToPixel(level, bbox.minX, bbox.maxY);
  const bottomRight = raster.crsToPixel(level, bbox.maxX, bbox.minY);

  const x0 = Math.floor(topLeft.px);
  const y0 = Math.floor(topLeft.py);
  const x1 = Math.ceil(bottomRight.px);
  const y1 = Math.ceil(bottomRight.py);
  const width = Math.max(1, x1 - x0);
  const height = Math.max(1, y1 - y0);

  // Texture rows are flipped on upload, so V is measured from the bottom of the window.
  const vTop = 1 - (topLeft.py - y0) / height;
  const vBottom = 1 - (bottomRight.py - y0) / height;

  return {
    window: [x0, y0, x1, y1],
    width,
    height,
    uv: {
      offsetX: (topLeft.px - x0) / width,
      offsetY: vBottom,
      repeatX: (bottomRight.px - topLeft.px) / width,
      repeatY: vTop - vBottom,
    },
  };
}

/** Output size for the whole-site texture, preserving aspect ratio. */
function baseSize(lvl: { width: number; height: number }): { outWidth: number; outHeight: number } {
  const scale = Math.min(1, BASE_TEXTURE_MAX / Math.max(lvl.width, lvl.height));
  return {
    outWidth: Math.max(1, Math.round(lvl.width * scale)),
    outHeight: Math.max(1, Math.round(lvl.height * scale)),
  };
}

function clampPow2(value: number, min: number, max: number): number {
  const rounded = 2 ** Math.ceil(Math.log2(Math.max(1, value)));
  return Math.min(max, Math.max(min, rounded));
}

function distanceToCentre(bbox: Bbox2, x: number, y: number): number {
  return Math.hypot((bbox.minX + bbox.maxX) / 2 - x, (bbox.minY + bbox.maxY) / 2 - y);
}

function toFloatArray(data: ArrayLike<number>, noData: number | null): Float32Array {
  const out = new Float32Array(data.length);
  for (let i = 0; i < data.length; i++) {
    out[i] = isNoData(data[i], noData) ? NaN : data[i];
  }
  return out;
}

